import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import api from '../../services/api';
import { setUser as setReduxUser } from '../../store/authSlice';
import SealLoader from './SealLoader';
import ColumnMark from '../chambers/ColumnMark';
import LegalModal from '../legal/LegalModal';

/**
 * Blocking gate between OAuth and the app (and the onboarding wizard —
 * consent comes first). Fires for brand-new signups AND every existing
 * user whose stored `terms_version` doesn't match the backend's current
 * `CURRENT_TERMS_VERSION` (app/constants/legal.py) — including a version
 * bump after a future ToS change. One check covers all three cases.
 *
 * Self-contained: fetches the current version itself rather than making
 * App.tsx thread it through, so this stays a drop-in the same way
 * SignupWizard is.
 */

interface LegalGateProps {
  onComplete: () => void;
}

export default function LegalGate({ onComplete }: LegalGateProps) {
  const dispatch = useDispatch();
  const profile = useSelector((s: RootState) => s.auth.user);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<'terms' | 'privacy' | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/meta/legal-version')
      .then(({ data }) => {
        if (!cancelled) setCurrentVersion(data.version);
      })
      .catch(() => {
        if (!cancelled) setFetchFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const alreadyAccepted =
    currentVersion !== null && profile?.terms_version === currentVersion;

  useEffect(() => {
    if (alreadyAccepted) onComplete();
  }, [alreadyAccepted, onComplete]);

  if (fetchFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-4">
        <div className="max-w-sm text-center">
          <p className="text-sm text-text-secondary mb-4">
            Couldn&rsquo;t reach iAdvocate to confirm the latest Terms of Service. Please
            check your connection and try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-legal-green-700 dark:text-legal-green-400 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (currentVersion === null || alreadyAccepted) {
    return <SealLoader words={['Checking your account']} />;
  }

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post('/auth/accept-terms', { version: currentVersion });
      if (profile) {
        dispatch(
          setReduxUser({
            ...profile,
            terms_accepted_at: data.terms_accepted_at,
            terms_version: data.terms_version,
          }),
        );
      }
      onComplete();
    } catch {
      setError('Could not record your acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4 py-10">
      <div className="max-w-lg w-full bg-surface-elevated border border-border-primary rounded-xl shadow-elevation-2 p-8">
        <ColumnMark className="h-9 w-auto text-legal-green-700 dark:text-legal-green-400 mb-6" />
        <h1 className="text-xl font-semibold text-text-primary mb-2">
          Before you continue
        </h1>
        <p className="text-sm text-text-secondary mb-4">
          iAdvocate uses AI to assist your legal research and drafting. Please review and
          accept the following before using the platform.
        </p>
        <ul className="text-sm mb-6 space-y-1 list-disc pl-5">
          <li>
            <button
              type="button"
              onClick={() => setOpenDoc('terms')}
              className="text-legal-green-700 dark:text-legal-green-400 underline"
            >
              Terms of Service
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => setOpenDoc('privacy')}
              className="text-legal-green-700 dark:text-legal-green-400 underline"
            >
              Privacy Policy
            </button>
          </li>
        </ul>
        <label className="flex items-start gap-3 text-sm text-text-primary mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1"
          />
          <span>
            I have read and agree to the Terms of Service and Privacy Policy, and I
            understand that AI-generated analysis on this platform must be independently
            verified before I rely on it for any legal proceeding or advice to a client.
          </span>
        </label>
        {/* Support Desk (2026-10-27): the support team may reach out by phone or
            WhatsApp. A notice, not a second checkbox, and not a Terms change —
            so terms_version is NOT bumped and existing acceptors are not
            re-prompted. */}
        <p className="text-sm text-text-secondary -mt-3 mb-6">
          Our team may call or WhatsApp you to help you get started.
        </p>
        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
        <button
          type="button"
          disabled={!checked || submitting}
          onClick={handleAccept}
          className="w-full py-2.5 rounded-md bg-legal-green-600 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-legal-green-700 transition-colors"
        >
          {submitting ? 'Saving…' : 'Agree and continue'}
        </button>
      </div>

      {openDoc && <LegalModal initialDoc={openDoc} onClose={() => setOpenDoc(null)} />}
    </div>
  );
}
